import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import {
  $Select,
  $Skeleton,
  $Tooltip,
  $Button,
  $Notification,
  $Empty,
  $Input,
  $Collapse,
  $Radio,
} from "us.common/components";
import {
  FollowUpPinStatus,
  IFollowUpItem,
} from "us.collection.followup/interfaces";
import { IFollowUpList } from "us.collection.followup/components/FollowUps/Components/FollowUpList/Interfaces";
import { followUps } from "us.collection.followup/actions";
import { FollowUpItem } from "us.collection.followup/components/FollowUps/Components/FollowUpList/Components";
import {
  ImportOutlined,
  LoadingOutlined,
  DownOutlined,
  UpOutlined,
} from "us.icons";
import { useSignalRTrigger } from "us.common/SignalRProvider";
import { NotificationTriggerType } from "us.common/constants/Component/Notification";
import { searchFollowup, filterSavedFollowUpFilters } from "./Functions";
import { PinType } from "./Constants";
import { useFormikContext } from "formik";
import { RootState } from "us.helper/types";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { SavedFiltersTabs } from "us.collection.followup/constants";

const {
  categories,
  list,
  result,
  reImport,
  followUpNavigation,
  categoryFilters,
  unpinnedItems,
} = followUps;

const FollowUpList: React.FC<PropsFromRedux & IFollowUpList> = (props) => {
  const {
    categories,
    list,
    isBulkFollowupReImporting,
    followUpNavigation,
    isFiltersFetching,
    AuthorizationProfile,
    caseHandlers,
    reImportReset,
    reImportFollowUp,
    getFollowUpCategories,
    getFollowUpList,
    updateNavigationDetails,
    setCategoryFilters,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { values } = useFormikContext<any>();
  const { followUpId, filterId } =
    useParams<{ followUpId?: string; filterId?: string }>();
  const [selectedFollowUp, setSelectedFollowUp] = useState<{
    followUpId: number;
    filterId?: number;
  }>({ followUpId: -1 });
  const { setFieldValue } = useFormikContext<any>();
  const { setTrigger } = useSignalRTrigger();

  const { followUpCategory } = values;
  const { data: categoryOptions, categorySearchText } = categories ?? {};
  const { isFetching, data } = list ?? {};

  const { pinnedFollowup, unpinnedFollowup } = useMemo(() => {
    return searchFollowup(data, categorySearchText);
  }, [data, categorySearchText]);

  const { isBack } = followUpNavigation ?? {};
  const currentUser: any = new Azure.ADAuth().currentUser();
  const triggers = [
    {
      name: NotificationTriggerType.ON_FOLLOWUP_REIMPORT,
      callBack: () => {
        $Notification.success({
          message: t(
            `US.COLLECTION.FOLLOWUP:MANUAL_FOLLOWUP.FOLLOWUPS_REIMPORTED_SUCCESSFULLY`
          ),
          key: "1",
          placement: "topRight",
          duration: 10,
        });
        reImportReset && reImportReset({});
      },
    },
  ];

  useEffect(() => {
    if (!isBack) {
      getFollowUpCategories && getFollowUpCategories({});
      setTrigger(triggers);
    }
    const targetElement = document.querySelector(
      `a[href="/follow-up/${followUpId}"]`
    );
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (followUpId) {
      if (filterId) {
        setSelectedFollowUp({
          followUpId: Number(followUpId),
          filterId: Number(filterId),
        });
      } else {
        setSelectedFollowUp({ followUpId: Number(followUpId) });
      }
    }
  }, [followUpId, filterId]);

  const currentUserEmail = useMemo(() => {
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile
    );
  }, [AuthorizationProfile]);

  useEffect(() => {
    if (followUpCategory != undefined && !isBack) {
      getFollowUpList &&
        getFollowUpList({
          categoryId: followUpCategory,
          pinStatus: FollowUpPinStatus.ALL,
          userEmail: currentUserEmail,
        });
    }
  }, [followUpCategory]);

  const filteredSavedFilters = useMemo(() => {
    return filterSavedFollowUpFilters(
      pinnedFollowup,
      currentUserEmail,
      values?.savedFilterTab,
      caseHandlers.data
    );
  }, [
    pinnedFollowup,
    currentUserEmail,
    values?.savedFilterTab,
    caseHandlers.data,
  ]);

  return (
    <div className="fl-sidebar">
      <div className="fl-sidebar-header">
        <div className="d-flex align-items-top">
          <div className="flex-grow-1">
            <$Select
              name="followUpCategory"
              size="small"
              options={categoryOptions}
              value={followUpCategory}
              allOption={false}
              className="w-100"
              optionText="categoryName"
              optionValue="categoryID"
              onSearchBy={["categoryName", "categoryID"]}
              dataTestid="followUpCategoryTestId"
              onSelect={(categoryId: number) => {
                isBack &&
                  updateNavigationDetails &&
                  updateNavigationDetails({
                    isBack: false,
                    viewedItems: [],
                  });
                navigate("/follow-up");
                setSelectedFollowUp({ followUpId: -1 });
              }}
            />
            <div className="pt-1">
              <$Input
                size="small"
                name="followupSearch"
                className="w-100"
                allowClear={true}
                placeholder={t("US.COMMON:COMMON.SEARCH")}
                onChange={(inputParam: any) => {
                  if (isBack)
                    updateNavigationDetails &&
                      updateNavigationDetails({
                        isBack: false,
                        viewedItems: [],
                      });
                  setCategoryFilters &&
                    setCategoryFilters(inputParam.target?.value);
                  setFieldValue("followupSearch", inputParam.target?.value);
                }}
              />
            </div>
          </div>
          <div>
            <$Tooltip
              title={t(
                "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.RE_IMPORT_ALL_FOLLOWUPS"
              )}
            >
              <$Button
                type="dashed"
                size="small"
                icon={
                  isBulkFollowupReImporting ? (
                    <LoadingOutlined
                      style={{
                        fontSize: 14,
                      }}
                      spin
                    />
                  ) : (
                    <ImportOutlined />
                  )
                }
                onClick={() =>
                  reImportFollowUp &&
                  reImportFollowUp({
                    followUpId: -1,
                  })
                }
                className="ml-3"
                disabled={isBulkFollowupReImporting}
              />
            </$Tooltip>
          </div>
        </div>
      </div>
      <$Skeleton
        className="p-3"
        loading={
          isFiltersFetching ||
          isFetching ||
          categories?.isFetching ||
          !followUpCategory
        }
        active
        paragraph={{ rows: 4 }}
      >
        <>
          {[...pinnedFollowup, ...unpinnedFollowup].length == 0 &&
            !isFetching && (
              <$Empty
                description={`${t("US.COLLECTION.COMMON:COMMON.NO_DATA")}`}
                image={$Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}

          <div className="fl-sidebar-body">
            <$Collapse
              bordered={false}
              defaultActiveKey={["1"]}
              expandIcon={({ isActive }) =>
                isActive ? <UpOutlined /> : <DownOutlined />
              }
              className="fl-sidebar-menu"
              accordion
            >
              <$Collapse.Panel
                header={t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SAVED_FILTERS")}
                key="1"
              >
                <div className="px-3 py-2 flsm-filters">
                  <$Radio
                    name="savedFilterTab"
                    options={[
                      {
                        value: SavedFiltersTabs.ASSIGNED,
                        label: t(
                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ASSIGNED"
                        ),
                      },
                      {
                        value: SavedFiltersTabs.CREATED,
                        label: t(
                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.CREATED"
                        ),
                      },
                      {
                        value: SavedFiltersTabs.ALL,
                        label: t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ALL"),
                      },
                    ]}
                    optionValue="value"
                    optionText="label"
                    optionStyle="align-items-center"
                    className="text-nowrap"
                  />
                </div>
                {filteredSavedFilters?.length == 0 && !isFetching && (
                  <$Empty
                    description={`${t("US.COLLECTION.COMMON:COMMON.NO_DATA")}`}
                    image={$Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
                {filteredSavedFilters.map(
                  (followUpItem: IFollowUpItem, index: number) => {
                    const { isFilter, followupId, filterId } =
                      followUpItem ?? {};
                    return (
                      <>
                        <FollowUpItem
                          followUpItem={followUpItem}
                          key={index}
                          onSelect={() => {
                            isBack &&
                              updateNavigationDetails &&
                              updateNavigationDetails({
                                isBack: false,
                                viewedItems: [],
                              });
                            setSelectedFollowUp({
                              followUpId: followupId,
                              filterId: Number(filterId),
                            });
                          }}
                          isSelected={
                            isFilter
                              ? filterId == selectedFollowUp.filterId
                              : followupId == selectedFollowUp.followUpId
                          }
                          type={PinType.PINNED}
                        />
                      </>
                    );
                  }
                )}
              </$Collapse.Panel>
              <$Collapse.Panel
                header={t(
                  "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PINNED_FOLLOWUPS"
                )}
                key="2"
              >
                {pinnedFollowup?.filter(({ isFilter }) => !isFilter).length ==
                  0 &&
                  !isFetching && (
                    <$Empty
                      description={`${t(
                        "US.COLLECTION.COMMON:COMMON.NO_DATA"
                      )}`}
                      image={$Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                {pinnedFollowup?.map(
                  (followUpItem: IFollowUpItem, index: number) => {
                    const { isFilter, followupId, filterId } =
                      followUpItem ?? {};
                    return (
                      <>
                        {!isFilter && (
                          <FollowUpItem
                            followUpItem={followUpItem}
                            key={index}
                            onSelect={() => {
                              isBack &&
                                updateNavigationDetails &&
                                updateNavigationDetails({
                                  isBack: false,
                                  viewedItems: [],
                                });
                              setSelectedFollowUp({
                                followUpId: followupId,
                                filterId: Number(filterId),
                              });
                            }}
                            isSelected={
                              isFilter
                                ? filterId == selectedFollowUp.filterId
                                : followupId == selectedFollowUp.followUpId
                            }
                            type={PinType.PINNED}
                          />
                        )}
                      </>
                    );
                  }
                )}
              </$Collapse.Panel>
              <$Collapse.Panel
                header={t(
                  "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.UNPINNED_FOLLOWUPS"
                )}
                key="3"
              >
                {unpinnedFollowup.length == 0 && !isFetching && (
                  <$Empty
                    description={`${t("US.COLLECTION.COMMON:COMMON.NO_DATA")}`}
                    image={$Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
                {unpinnedFollowup?.map(
                  (followUpItem: IFollowUpItem, index: number) => {
                    const { followupId } = followUpItem;
                    return (
                      <FollowUpItem
                        followUpItem={followUpItem}
                        key={index}
                        onSelect={() => {
                          isBack &&
                            updateNavigationDetails &&
                            updateNavigationDetails({
                              isBack: false,
                              viewedItems: [],
                            });
                          setSelectedFollowUp({ followUpId: followupId });
                        }}
                        isSelected={followupId == selectedFollowUp.followUpId}
                        type={PinType.UNPINNED}
                      />
                    );
                  }
                )}
              </$Collapse.Panel>
            </$Collapse>
          </div>
        </>
      </$Skeleton>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { followUps, AuthorizationProfile } = state;
  const {
    categories,
    list,
    isBulkFollowupReImporting,
    followUpNavigation,
    isFiltersFetching,
    pinStatus,
    caseHandlers,
  } = followUps;
  return {
    categories,
    list,
    isBulkFollowupReImporting,
    followUpNavigation,
    isFiltersFetching,
    pinStatus,
    AuthorizationProfile,
    caseHandlers,
  };
};

const mapDispatchToProps = {
  getFollowUpCategories: categories.get,
  getFollowUpList: list.get,
  getFollowUpResult: result.get,
  reImportFollowUp: reImport.execute,
  reImportReset: reImport.reset,
  updateNavigationDetails: followUpNavigation.set,
  setUnpinnedExpanded: unpinnedItems.set,
  setCategoryFilters: categoryFilters.set,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FollowUpList);
